import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { App as CapacitorApp } from '@capacitor/app';

import RoisPhotoPermissions, {
  PhotoPermissionsStatus,
} from '../plugins/roisPhotoPermissions';
import RoisBackgroundLocation from '../plugins/roisBackgroundLocation';
import { isNativeApp } from '../utils/positionSource';
import {
  isSystemPromptOpen,
  onSystemPromptClosed,
  onSystemPromptOpened,
} from '../utils/systemPrompts';
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds, ModalStateType } from '../components/modals/SuperModal/types';
import { getCurrentModal } from '../state/selectors/ui/modal';
import {
  PhotoPermissionMode,
  PhotoPermissionStep,
  PhotoPermissionsRequiredModalData,
} from '../components/modals/submodals/PhotoPermissionsRequiredModal/types';

export interface PhotoPermissionsGateOptions {
  /** Flag `feature-photo-geotag` del tenant (D17). Sin él no se pide nada. */
  enabled: boolean;
  /** Tramo de recorrido en curso: no se le tapa la pantalla a quien graba (D18). */
  hasActiveSegment: boolean;
  /**
   * Esta tablet tiene un recorrido en curso que grabar. Su guardián del GPS
   * arranca el servicio nativo y ese `start()` PIDE a Android la ubicación y
   * las notificaciones por su cuenta: mientras el GPS del recorrido no esté
   * grabando, este guardián espera (ver `ROUTE_START_POLL_MS`).
   */
  routeCapturing?: boolean;
  /**
   * Ya se sabe qué recorridos hay activos (y por tanto si esta tablet graba
   * uno). Mientras no, se espera — con tope, ver `ROUTES_UNKNOWN_MAX_WAIT_MS`.
   */
  routesKnown?: boolean;
}

interface PendingStep {
  step: PhotoPermissionStep;
  mode: PhotoPermissionMode;
  wrongChoice: boolean;
}

/**
 * Si una petición de ubicación contesta antes de esto, Android no enseñó
 * ningún diálogo: una persona tarda más en leerlo y elegir.
 */
export const NO_DIALOG_BELOW_MS = 700;

/**
 * Cada cuánto se vuelve a mirar si el GPS del recorrido ya graba, mientras se
 * espera a que termine de pedir sus permisos. El `resume` al cerrarse los
 * diálogos de Android también vuelve a mirar; esto cubre que el servicio
 * arranque un poco después.
 */
export const ROUTE_START_POLL_MS = 3000;

/**
 * Tope de espera a saber si esta tablet graba un recorrido. 🔴 Sin esperar,
 * al entrar salía «Activa la ubicación» y desaparecía en cuanto llegaba la
 * lista de recorridos (visto en la W el 25-09-2026). Con tope porque sin red
 * al entrar la lista puede no llegar nunca, y un permiso pedido tarde es mejor
 * que uno que no se pide.
 */
export const ROUTES_UNKNOWN_MAX_WAIT_MS = 10_000;

/**
 * Qué falta, en orden: primero la ubicación precisa y luego las fotos. `null`
 * si no falta nada.
 *
 * 🔴 Con la ubicación APROXIMADA, Capacitor da `denied` aunque Android SÍ
 * vuelve a enseñar un diálogo («¿Cambiar … de aproximada a precisa?», medido
 * en la Tab Active5 el 25-09-2026). Por eso con la aproximada se vuelve a
 * pedir, y solo se manda a Ajustes cuando la última petición contestó sin
 * diálogo (`approximateWithoutDialog`).
 */
export function nextPendingStep(
  status: PhotoPermissionsStatus,
  approximateWithoutDialog = false,
): PendingStep | null {
  const { location, photos } = status;
  if (location.state !== 'granted') {
    const approximate = location.approximate === true;
    const settings = approximate
      ? approximateWithoutDialog
      : location.state === 'denied';
    return {
      step: 'location',
      mode: settings ? 'settings' : 'ask',
      wrongChoice: approximate,
    };
  }
  if (photos.required && photos.state !== 'granted') {
    return {
      step: 'photos',
      mode: photos.state === 'denied' ? 'settings' : 'ask',
      wrongChoice: photos.limited === true,
    };
  }
  return null;
}

/**
 * Exige, en la APK, los permisos que necesita una foto para guardar DÓNDE se
 * hizo. Spec 2026-09-23 fotos geolocalizadas, § 14 y § 15.
 *
 *  1. **Ubicación precisa** — para la cámara de ROIS. Sin ella la foto salía
 *     sin GPS y sin avisar (medido el 24-09-2026). La aproximada no vale: con
 *     kilómetros de error el filtro de 200 m la descarta.
 *  2. **Ubicación de las fotos** (`ACCESS_MEDIA_LOCATION`) — para la galería:
 *     sin ella Android entrega las fotos con el GPS a ceros.
 *
 * De uno en uno y en ese orden, en el mismo modal.
 *
 * ─── Cómo pide ─────────────────────────────────────────────────────────────
 *
 * Primero NUESTRO modal, y el diálogo de Android solo al pulsar «Continuar».
 * Cuando Android ya no enseña el diálogo, el modal pasa a mandar a Ajustes.
 *
 * 🔴 Al volver a la app SOLO se comprueba. Pedir un permiso negado para siempre
 * contesta al instante, pero la app pasa igual por pausa y vuelta: pedirlo
 * desde el `resume` sería un bucle que tumba la app (medido con el GPS, cien
 * llamadas por segundo). Pedir es cosa del botón.
 *
 * ─── Cuándo no bloquea ─────────────────────────────────────────────────────
 *
 * En web; sin el flag; con un tramo en curso (espera a que se cierre, como la
 * actualización obligatoria); y en una APK vieja sin el plugin, que recibe este
 * código por OTA antes que la APK nueva. Encerrar a alguien detrás de un
 * permiso que no puede conceder sería peor que la foto sin GPS.
 *
 * Y nunca pisa otro modal: `openModal` REEMPLAZA el que hubiera. Si hay uno
 * abierto, espera a que se cierre. Para el rol Tablet eso fija el orden al
 * entrar: ubicación → fotos → «Permitir siempre» (el de segundo plano espera
 * su turno igual, ver `useBackgroundLocationGate`).
 */
export function usePhotoPermissionsGate({
  enabled,
  hasActiveSegment,
  routeCapturing = false,
  routesKnown = true,
}: PhotoPermissionsGateOptions): void {
  const dispatch = useDispatch();
  // Solo el id: este hook vive en el `TimerOverlay`, montado en el Header de
  // toda la app, y el modal completo cambia con cualquier dato interno.
  const currentModalId = useSelector(
    (state: unknown) => (getCurrentModal(state) as ModalStateType | undefined)?.id
  );

  // Refs además de las props: el listener de `resume` y los botones del modal
  // se crean una vez y leerían valores congelados.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const activeRef = useRef(hasActiveSegment);
  activeRef.current = hasActiveSegment;
  const routeCapturingRef = useRef(routeCapturing);
  routeCapturingRef.current = routeCapturing;
  const routesKnownRef = useRef(routesKnown);
  routesKnownRef.current = routesKnown;
  // Desde cuándo se espera a la lista de recorridos (ver el tope).
  const mountedAt = useRef(Date.now());
  // El reintento mientras el GPS del recorrido arranca.
  const routeRetry = useRef<ReturnType<typeof setTimeout> | null>(null);
  // La app está en primer plano SIN nada del sistema encima. Un diálogo de
  // permisos de Android la pone en pausa (`pause`) hasta que se contesta
  // (`resume`); la página del WebView no se entera (sigue «visible» y con el
  // foco: medido en la W el 25-09-2026).
  const appActive = useRef(true);
  const visibleModalId = useRef(currentModalId);
  visibleModalId.current = currentModalId;

  // Quién abrió el modal y qué enseña (paso + modo + opción mala). Sin esto,
  // cerrarlo al conceder el permiso cerraría cualquier otro modal que hubiera
  // en pantalla.
  const openedByUs = useRef(false);
  const shownKey = useRef<string | null>(null);
  // Cada comprobación lleva número: si mientras espera al plugin empieza otra,
  // la vieja no decide nada.
  const lastCheck = useRef(0);
  // Con la aproximada, la última petición de ubicación contestó sin diálogo:
  // Android ya no lo enseña y solo queda Ajustes (ver `nextPendingStep`).
  const approximateWithoutDialog = useRef(false);
  // Hay una petición NUESTRA en curso: su diálogo de Android sale encima del
  // aviso a propósito (el aviso dice qué opción tocar en él), así que ese
  // diálogo no lo retira. Los de los demás, sí (ver abajo).
  const ownRequest = useRef(false);

  const closeIfMine = useCallback(() => {
    if (!openedByUs.current) return;
    openedByUs.current = false;
    shownKey.current = null;
    dispatch(closeModal());
  }, [dispatch]);

  // `check` y los botones se necesitan entre sí; la ref rompe el ciclo.
  const checkRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const request = useCallback((step: PhotoPermissionStep) => {
    const startedAt = Date.now();
    ownRequest.current = true;
    const ask =
      step === 'location'
        ? RoisPhotoPermissions.requestLocation()
        : RoisPhotoPermissions.requestPhotos();
    void ask
      .then((result) => {
        if (step !== 'location') return;
        approximateWithoutDialog.current =
          result?.location?.approximate === true &&
          Date.now() - startedAt < NO_DIALOG_BELOW_MS;
      })
      .catch(() => {
        // Si el diálogo no llega a salir, el modal sigue y el botón también:
        // se puede volver a intentar, y Ajustes siempre queda.
      })
      .finally(() => {
        ownRequest.current = false;
        void checkRef.current();
      });
  }, []);

  const openSettings = useCallback(() => {
    void RoisBackgroundLocation.openAppSettings().catch(() => {
      // Sin Ajustes no hay nada que ofrecer: el modal sigue y el operario
      // tendrá que llegar a mano.
    });
  }, []);

  const check = useCallback(async () => {
    const id = ++lastCheck.current;
    if (routeRetry.current) {
      clearTimeout(routeRetry.current);
      routeRetry.current = null;
    }
    if (!isNativeApp() || !enabledRef.current || activeRef.current) {
      closeIfMine();
      return;
    }

    // Aún no se sabe si esta tablet graba un recorrido: si lo graba, es el
    // recorrido quien pide primero. Se espera a saberlo, con tope.
    if (!routesKnownRef.current) {
      const waited = Date.now() - mountedAt.current;
      if (waited < ROUTES_UNKNOWN_MAX_WAIT_MS) {
        closeIfMine();
        routeRetry.current = setTimeout(
          () => void checkRef.current(),
          ROUTES_UNKNOWN_MAX_WAIT_MS - waited,
        );
        return;
      }
    }

    // 🔴 Con un recorrido que esta tablet está grabando, primero pide el
    // recorrido. Medido en la W el 25-09-2026 (logcat): al entrar, el guardián
    // del GPS del recorrido llama a `start()` 0,6 s después de abrirse este
    // modal, y Android sacaba SUS diálogos de ubicación y de notificaciones
    // encima del nuestro. Se espera a que su GPS grabe (entonces sus permisos
    // ya están resueltos) y se pide lo que falte. Una APK que no sepa decir si
    // graba no deja esperando para siempre.
    if (routeCapturingRef.current) {
      let running = true;
      try {
        running = (await RoisBackgroundLocation.isRunning()).running;
      } catch {
        running = true;
      }
      if (id !== lastCheck.current) return;
      if (!running && routeCapturingRef.current) {
        closeIfMine();
        routeRetry.current = setTimeout(() => void checkRef.current(), ROUTE_START_POLL_MS);
        return;
      }
    }

    let status: PhotoPermissionsStatus;
    try {
      status = await RoisPhotoPermissions.status();
    } catch {
      // APK vieja sin el plugin, o un fallo del puente: no se bloquea.
      if (id === lastCheck.current) closeIfMine();
      return;
    }
    if (id !== lastCheck.current) return;
    // Mientras se esperaba al plugin pudo empezar un tramo o apagarse el flag.
    if (!enabledRef.current || activeRef.current) {
      closeIfMine();
      return;
    }

    if (status.location.state === 'granted' || !status.location.approximate) {
      approximateWithoutDialog.current = false;
    }
    const pending = nextPendingStep(status, approximateWithoutDialog.current);
    if (!pending) {
      closeIfMine();
      return;
    }

    const key = `${pending.step}:${pending.mode}:${pending.wrongChoice}`;
    if (openedByUs.current && shownKey.current === key) return;
    if (!openedByUs.current && visibleModalId.current) return;
    // 🔴 Nunca se abre (ni se cambia de paso) con un diálogo del sistema
    // encima, lo haya pedido quien sea: el recorrido, la cámara… El `resume`
    // al cerrarse vuelve a mirar. Medido en la W el 25-09-2026: el recorrido
    // arrancó su GPS con el diálogo de notificaciones aún abierto, y el aviso
    // de fotos salió debajo. (Cerrar el nuestro, arriba, no espera a nada.)
    if (!appActive.current) return;
    // Tampoco con la hoja de Google «¿Guardar contraseña?» encima, que no pone
    // la app en pausa (ver `systemPrompts`). Al cerrarse, se vuelve a mirar.
    if (isSystemPromptOpen()) return;

    openedByUs.current = true;
    shownKey.current = key;
    const data: PhotoPermissionsRequiredModalData = {
      ...pending,
      onRequest: () => request(pending.step),
      onOpenSettings: openSettings,
    };
    dispatch(openModal(modalIds.PHOTO_PERMISSIONS_REQUIRED, data));
  }, [closeIfMine, dispatch, openSettings, request]);
  checkRef.current = check;

  useEffect(() => {
    void check();
  }, [check, enabled, hasActiveSegment, routeCapturing, routesKnown]);

  // El hueco de modal cambió. Si el nuestro ya no está, otro lo reemplazó o
  // lo cerró: deja de ser nuestro. Y si el hueco queda libre, es el momento
  // de volver a mirar (así se abre cuando el operario cierra el suyo).
  useEffect(() => {
    if (
      openedByUs.current &&
      currentModalId !== modalIds.PHOTO_PERMISSIONS_REQUIRED
    ) {
      openedByUs.current = false;
      shownKey.current = null;
    }
    if (!currentModalId) void checkRef.current();
  }, [currentModalId]);

  // Android no avisa al conceder un permiso: volver de Ajustes (o del diálogo)
  // es la señal. Solo se COMPRUEBA, nunca se pide (ver arriba). Y `pause`
  // marca que hay algo del sistema encima: mientras dure, no se abre nada.
  useEffect(() => {
    if (!isNativeApp()) return;
    const handles: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;
    const keep = (h: { remove: () => Promise<void> }) => {
      if (cancelled) {
        void h.remove();
        return;
      }
      handles.push(h);
    };
    void CapacitorApp.addListener('pause', () => {
      appActive.current = false;
    }).then(keep);
    void CapacitorApp.addListener('resume', () => {
      appActive.current = true;
      void checkRef.current();
    }).then(keep);
    return () => {
      cancelled = true;
      handles.forEach((h) => void h.remove());
    };
  }, []);

  // Al cerrarse la hoja de guardar contraseña, es el turno de mirar.
  useEffect(() => onSystemPromptClosed(() => void checkRef.current()), []);

  // Si otro saca algo del sistema encima del nuestro (el GPS del recorrido,
  // la cámara…), el nuestro se retira: al volver se mira otra vez y, si sigue
  // faltando, vuelve a salir. El diálogo que pide nuestro «Continuar» no.
  useEffect(
    () =>
      onSystemPromptOpened(() => {
        if (!ownRequest.current) closeIfMine();
      }),
    [closeIfMine],
  );

  // Si el componente que lo usa se desmonta, el modal no puede quedarse en
  // pantalla sin nadie que lo cierre: es bloqueante. Y el reintento se apaga.
  useEffect(
    () => () => {
      if (routeRetry.current) clearTimeout(routeRetry.current);
      closeIfMine();
    },
    [closeIfMine],
  );
}

export default usePhotoPermissionsGate;
